// Admin — Crisp 客服中心（2026-06-29）
// 統計卡 + 篩選（狀態/分類/來源/日期/關鍵字）+ 對話列表 + 詳情對話框（訊息 + 狀態/分類/備註）+ XLSX 匯出
import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Divider, Box, Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const PAGE_SIZE = 20;

const apiCall = (url, method, body, token) =>
  fetch(url, {
    method: method || "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const STATUS_META = {
  NEW: { label: "新對話", color: "secondary" },
  OPEN: { label: "待處理", color: "warning" },
  PROCESSING: { label: "處理中", color: "info" },
  RESOLVED: { label: "已解決", color: "success" },
  UNRESOLVED: { label: "未解決", color: "error" },
  FOLLOW_UP: { label: "需追蹤", color: "primary" },
  CLOSED: { label: "已關閉", color: "default" },
};
const SOURCE_META = {
  official_site: "官網",
  trade_platform: "交易平台",
  unknown: "未知",
};

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

function SupportCenterAdmin() {
  const { token } = useAuth();

  const [meta, setMeta] = useState({ statuses: [], categories: [], sourceSites: [] });
  const [stats, setStats] = useState(null);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [keyword, setKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const [detail, setDetail] = useState(null); // { conversation, messages, histories }
  const [noteInput, setNoteInput] = useState("");
  const [saving, setSaving] = useState(false);

  // ── 共用 query string ──
  const buildQS = useCallback(() => {
    const qs = new URLSearchParams();
    if (statusFilter !== "ALL") qs.set("status", statusFilter);
    if (categoryFilter !== "ALL") qs.set("category", categoryFilter);
    if (sourceFilter !== "ALL") qs.set("sourceSite", sourceFilter);
    if (dateFrom) qs.set("dateFrom", dateFrom);
    if (dateTo) qs.set("dateTo", dateTo);
    if (keyword) qs.set("keyword", keyword);
    return qs;
  }, [statusFilter, categoryFilter, sourceFilter, dateFrom, dateTo, keyword]);

  // ── fetch ──
  const fetchMeta = useCallback(() => {
    apiCall("/api/admin/support/meta", "GET", null, token).then((d) => d && setMeta(d)).catch(() => {});
  }, [token]);

  const fetchStats = useCallback(() => {
    apiCall("/api/admin/support/stats", "GET", null, token).then((d) => setStats(d || null)).catch(() => {});
  }, [token]);

  const fetchList = useCallback(() => {
    const qs = buildQS();
    qs.set("page", String(page + 1));
    qs.set("pageSize", String(PAGE_SIZE));
    apiCall(`/api/admin/support/conversations?${qs.toString()}`, "GET", null, token)
      .then((d) => {
        setRows(Array.isArray(d?.records) ? d.records : []);
        setTotal(d?.total || 0);
      })
      .catch(() => { setRows([]); setTotal(0); });
  }, [buildQS, page, token]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const applyFilter = () => { setKeyword(keywordInput); setPage(0); };

  // ── detail ──
  const openDetail = async (id) => {
    const d = await apiCall(`/api/admin/support/conversations/${id}`, "GET", null, token);
    setDetail(d);
    setNoteInput(d?.conversation?.note || "");
  };
  const closeDetail = () => { setDetail(null); };

  const refreshAfterChange = (d) => {
    setDetail(d);
    setNoteInput(d?.conversation?.note || "");
    fetchList();
    fetchStats();
  };

  const changeStatus = async (status) => {
    if (!detail?.conversation) return;
    setSaving(true);
    try {
      const d = await apiCall(`/api/admin/support/conversations/${detail.conversation.id}/status`, "PATCH", { status }, token);
      if (d?.conversation) refreshAfterChange(d); else window.alert(d?.message || "更新失敗");
    } finally { setSaving(false); }
  };

  const changeCategory = async (category) => {
    if (!detail?.conversation) return;
    setSaving(true);
    try {
      const d = await apiCall(`/api/admin/support/conversations/${detail.conversation.id}/category`, "PATCH", { category }, token);
      if (d?.conversation) refreshAfterChange(d); else window.alert(d?.message || "更新失敗");
    } finally { setSaving(false); }
  };

  const saveNote = async () => {
    if (!detail?.conversation) return;
    setSaving(true);
    try {
      const d = await apiCall(`/api/admin/support/conversations/${detail.conversation.id}/note`, "PATCH", { note: noteInput }, token);
      if (d?.conversation) refreshAfterChange(d); else window.alert(d?.message || "更新失敗");
    } finally { setSaving(false); }
  };

  // ── 匯出（帶 auth 的 blob 下載）──
  const doExport = async () => {
    const qs = buildQS();
    const res = await fetch(`/api/admin/support/export?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      window.alert(j?.message || "匯出失敗");
      return;
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const fileName = m ? m[1] : "crisp-support.xlsx";
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusOptions = ["ALL", ...(meta.statuses || [])];
  const categoryOptions = ["ALL", ...(meta.categories || [])];
  const sourceOptions = ["ALL", ...(meta.sourceSites || [])];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* 統計卡 */}
        <Grid container spacing={2} columns={10} mb={2}>
          {[
            { label: "今日對話", value: stats?.todayCount },
            { label: "待處理", value: stats?.pendingCount },
            { label: "已解決", value: stats?.resolvedCount },
            { label: "未解決", value: stats?.unresolvedCount },
            { label: "平均解決(時)", value: stats?.avgResolveHours ?? "—" },
          ].map((c) => (
            <Grid item xs={5} md={2} key={c.label}>
              <Card sx={{ p: 2, textAlign: "center" }}>
                <MDTypography variant="h4" fontWeight="bold">{c.value ?? "—"}</MDTypography>
                <MDTypography variant="caption" color="text">{c.label}</MDTypography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 列表 */}
        <Card>
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}
          >
            <MDTypography variant="h6" color="white">客服中心（Crisp）</MDTypography>
            <MDButton variant="contained" color="white" size="small" onClick={doExport}
              sx={{ color: "#F36D26 !important", fontWeight: 700 }}>
              匯出 XLSX
            </MDButton>
          </MDBox>

          {/* 篩選列 */}
          <MDBox px={2} pt={3} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="狀態" value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} sx={{ minWidth: 130 }} InputLabelProps={{ shrink: true }}>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>{s === "ALL" ? "全部狀態" : (STATUS_META[s]?.label || s)}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="分類" value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }} sx={{ minWidth: 140 }} InputLabelProps={{ shrink: true }}>
              {categoryOptions.map((c) => (
                <MenuItem key={c} value={c}>{c === "ALL" ? "全部分類" : c}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="來源" value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }} sx={{ minWidth: 120 }} InputLabelProps={{ shrink: true }}>
              {sourceOptions.map((s) => (
                <MenuItem key={s} value={s}>{s === "ALL" ? "全部來源" : (SOURCE_META[s] || s)}</MenuItem>
              ))}
            </TextField>
            <TextField type="date" size="small" label="起" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
            <TextField type="date" size="small" label="迄" value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} />
            <MDInput placeholder="搜尋 名稱 / Email / 主旨 / SessionID" value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilter(); }} size="small" sx={{ minWidth: 240 }} />
            <MDButton variant="gradient" color="info" size="small" onClick={applyFilter}>篩選</MDButton>
          </MDBox>

          <MDBox pt={2} px={2} pb={2}>
            <TableContainer sx={{ boxShadow: "none" }}>
              <Table>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>最後更新</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>來源</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>使用者</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>最後訊息</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>分類</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>狀態</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>目前尚無對話</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => {
                      const sm = STATUS_META[r.status] || STATUS_META.NEW;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{fmt(r.lastMessageAt || r.createdAt)}</TableCell>
                          <TableCell>{SOURCE_META[r.sourceSite] || r.sourceSite || "-"}</TableCell>
                          <TableCell>
                            <MDTypography variant="button" fontWeight="medium" display="block">{r.visitorName || "訪客"}</MDTypography>
                            <MDTypography variant="caption" color="text">{r.visitorEmail || "-"}</MDTypography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260 }}>
                            <MDTypography variant="caption" color="text" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {r.lastMessage || "-"}
                            </MDTypography>
                          </TableCell>
                          <TableCell>{r.category || <MDTypography variant="caption" color="text">未分類</MDTypography>}</TableCell>
                          <TableCell><Chip label={sm.label} color={sm.color} size="small" variant="outlined" /></TableCell>
                          <TableCell align="center">
                            <MDButton size="small" variant="outlined" color="info" onClick={() => openDetail(r.id)}>查看 / 處理</MDButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <MDTypography variant="caption" color="text">共 {total} 筆</MDTypography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" disabled={page <= 0} onClick={() => setPage((p) => p - 1)}><ArrowBackIcon fontSize="small" /></IconButton>
                <MDTypography variant="caption">{page + 1} / {totalPages}</MDTypography>
                <IconButton size="small" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}><ArrowForwardIcon fontSize="small" /></IconButton>
              </Box>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* detail dialog */}
      <Dialog open={!!detail} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>對話詳情</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          {detail?.conversation && (
            <Grid container spacing={2} pt={1}>
              {/* 左：對話內容 */}
              <Grid item xs={12} md={7}>
                <MDTypography variant="button" fontWeight="medium">對話內容</MDTypography>
                <Box sx={{ mt: 1, maxHeight: 420, overflowY: "auto", pr: 1 }}>
                  {(detail.messages || []).length === 0 ? (
                    <MDTypography variant="caption" color="text">尚無訊息內容（可能等待 REST 回拉）</MDTypography>
                  ) : (
                    detail.messages.map((m) => {
                      const isVisitor = m.senderType === "visitor";
                      return (
                        <Box key={m.id} sx={{ display: "flex", justifyContent: isVisitor ? "flex-start" : "flex-end", mb: 1 }}>
                          <Box sx={{
                            maxWidth: "80%", p: 1.2, borderRadius: 2,
                            bgcolor: isVisitor ? "grey.100" : "info.main",
                            color: isVisitor ? "text.primary" : "#fff",
                          }}>
                            <MDTypography variant="caption" sx={{ color: isVisitor ? "text.secondary" : "rgba(255,255,255,0.85)", display: "block" }}>
                              {m.senderName || (isVisitor ? "訪客" : "客服")} · {fmt(m.sentAt)}
                            </MDTypography>
                            <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14 }}>{m.content || `[${m.messageType || "非文字訊息"}]`}</Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Grid>

              {/* 右：資訊 + 操作 */}
              <Grid item xs={12} md={5}>
                <Stack spacing={1.2}>
                  <Row label="使用者" value={detail.conversation.visitorName || "訪客"} />
                  <Row label="Email" value={detail.conversation.visitorEmail || "-"} />
                  <Row label="電話" value={detail.conversation.visitorPhone || "-"} />
                  <Row label="來源" value={SOURCE_META[detail.conversation.sourceSite] || detail.conversation.sourceSite || "-"} />
                  <Row label="主旨" value={detail.conversation.subject || "-"} />
                  <Row label="Session" value={detail.conversation.crispSessionId} />
                  <Row label="滿意度" value={detail.conversation.rating != null ? `${detail.conversation.rating} ★` : "-"} />
                  <Divider />

                  <MDTypography variant="button" fontWeight="medium">狀態</MDTypography>
                  <Box display="flex" gap={0.8} flexWrap="wrap">
                    {(meta.statuses || []).map((s) => {
                      const sm = STATUS_META[s] || {};
                      const active = detail.conversation.status === s;
                      return (
                        <MDButton key={s} size="small" variant={active ? "gradient" : "outlined"}
                          color={sm.color === "default" ? "secondary" : (sm.color || "secondary")}
                          disabled={saving} onClick={() => changeStatus(s)}>
                          {sm.label || s}
                        </MDButton>
                      );
                    })}
                  </Box>

                  <MDTypography variant="button" fontWeight="medium" sx={{ mt: 1 }}>分類</MDTypography>
                  <TextField select size="small" value={detail.conversation.category || ""} disabled={saving}
                    onChange={(e) => changeCategory(e.target.value)} fullWidth>
                    <MenuItem value="">未分類</MenuItem>
                    {(meta.categories || []).map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
                  </TextField>

                  <MDTypography variant="button" fontWeight="medium" sx={{ mt: 1 }}>客服備註</MDTypography>
                  <TextField value={noteInput} onChange={(e) => setNoteInput(e.target.value)}
                    size="small" fullWidth multiline rows={3} />
                  <MDButton size="small" color="info" variant="gradient" disabled={saving} onClick={saveNote}>
                    {saving ? "儲存中..." : "儲存備註"}
                  </MDButton>

                  {(detail.histories || []).length > 0 && (
                    <>
                      <Divider />
                      <MDTypography variant="button" fontWeight="medium">處理紀錄</MDTypography>
                      <Box sx={{ maxHeight: 120, overflowY: "auto" }}>
                        {detail.histories.map((h) => (
                          <MDTypography key={h.id} variant="caption" color="text" display="block">
                            {fmt(h.createdAt)} · {h.action}
                            {h.metadata?.from ? `（${h.metadata.from}→${h.metadata.to || ""}）` : ""}
                          </MDTypography>
                        ))}
                      </Box>
                    </>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={closeDetail} color="secondary">關閉</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

function Row({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
      <MDTypography variant="caption" color="text" sx={{ minWidth: 64 }}>{label}</MDTypography>
      <Box sx={{ textAlign: "right", wordBreak: "break-all" }}>
        <MDTypography variant="button" fontWeight="medium">{value}</MDTypography>
      </Box>
    </Box>
  );
}

export default SupportCenterAdmin;
